import {
  MENTORING_OFFER_EMAIL_DELIVERY,
  type UpsertMentoringOfferOutputDTO
} from '@algoacademy/backend/modules/payment/endpoints/mutations/upsert-mentoring-offer/output.dto'

export type ShownOfferLinkVariant = 'sent' | 'emailFailed' | 'unpaid' | 'paid'

export type ShownOfferLink = {
  offerUrl: string
  variant: ShownOfferLinkVariant
}

/** Only the field this helper reads, so a test can build one without a whole DTO. */
type UnpaidOfferLike = { offerUrl: string } | null | undefined

/** Only the field this helper reads, so a test can build one without a whole DTO. */
type CurrentOfferLike = { offerUrl: string } | null | undefined

/**
 * Which `/mentoring/<id>` link the form has to show, and what it means right
 * now. Three sources can each hold a link for the same student, and only one
 * shows at a time - highest precedence first:
 *
 * 1. A link staff just minted by submitting this form. It is what staff is
 *    looking at the screen for, so it always wins over anything the lookup
 *    already knew about this student.
 * 2. The student's unpaid offer. A student can never carry an unpaid offer
 *    and a live, past-due or cancelled subscription at the same time - the
 *    offer is what turns into that subscription once it is paid - so this and
 *    the next case never actually compete for the same student.
 * 3. The paid offer behind `current`. This is the case that used to be
 *    missing entirely: once a student has a subscription, `unpaidOffer` comes
 *    back `null` and staff saw no link at all, even though the same durable
 *    address still exists and still opens something useful for the buyer.
 *
 * Returns null when none of the three apply - a fresh lookup with no offer of
 * any kind yet.
 */
export function readShownOfferLink(
  submittedOffer: UpsertMentoringOfferOutputDTO | null,
  unpaidOffer: UnpaidOfferLike,
  current: CurrentOfferLike
): ShownOfferLink | null {
  if (submittedOffer !== null) {
    return {
      offerUrl: submittedOffer.offerUrl,
      variant:
        submittedOffer.emailDelivery === MENTORING_OFFER_EMAIL_DELIVERY.SENT
          ? 'sent'
          : 'emailFailed'
    }
  }

  if (unpaidOffer !== null && unpaidOffer !== undefined) {
    return { offerUrl: unpaidOffer.offerUrl, variant: 'unpaid' }
  }

  if (current !== null && current !== undefined) {
    return { offerUrl: current.offerUrl, variant: 'paid' }
  }

  return null
}
